// Purchase orders: every PO with vendor and status filters (kept in the URL, so a vendor's page can
// link to its POs), and the open PO in a wide panel where its lines edit like a spreadsheet.
// Follow-up (shared/po-followup.ts): Next due (ship date, then arrival; red once past), the vendor's Answer
// (Acknowledged / Replied 2h ago / No answer 3d, linking to the Gmail thread), Email problems, and the
// "Needs follow-up" filter (?status=follow).
// Order updates --- They say: the vendor's ship date (amber when it's later than asked) and "N new" for their updates
// nobody has opened (?status=news, the sidebar's count).
import { Check, ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";
import { planToday } from "../../../../shared/day.ts";
import type { Po } from "../../../../shared/db.gen.ts";
import { isOpen, PO_KIND_LABELS, PO_STATUS_LABELS, PO_STATUSES, poTotals } from "../../../../shared/po.ts";
import { byPo, type FollowUp, followUp, NO_ANSWER_DAYS } from "../../../../shared/po-followup.ts";
import { ago, agoShort, dayText, money, plural, qty } from "../../lib/format.ts";
import { can, mutateWithResult } from "../../lib/mutate.ts";
import { setSearch, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { CreateDialog } from "../../ui/CreateDialog.tsx";
import { Badge, cx, Select } from "../../ui/controls.tsx";
import type { Column } from "../../ui/DataTable.tsx";
import { ListPage } from "../../ui/ListPage.tsx";
import { toast } from "../../ui/toast.tsx";
import { createPo, daysLate, STATUS_TONE, useLinesByPo, vendorLabel } from "./lib.ts";
import { useUnseenUpdates } from "./PoUpdates.tsx";

// Consignment --- a make PO to a CM (builds are added on its Make tab, each with its BOM snapshot).
async function createMakePo(vendor: { id: string }) {
  const id = crypto.randomUUID();
  const { ok } = await mutateWithResult("po.create_make", { id, vendor_id: vendor.id, lines: [] });
  if (ok) setSearch({ open: id });
  return ok;
}

type Row = Po & { vendor: string; lines: number; units: number; total: number; late: number; follow: FollowUp; news: number };

const STATUS_FILTERS = [
  { value: "open", label: "Open" },
  { value: "late", label: "Late" },
  { value: "follow", label: "Needs follow-up" },
  { value: "news", label: "New from vendors" },
  { value: "all", label: "All statuses" },
  ...PO_STATUSES.map((s) => ({ value: s, label: PO_STATUS_LABELS[s] })),
];

export function PurchaseOrdersPage() {
  const params = useSearch();
  const status = params.get("status") ?? "open";
  const vendorId = params.get("vendor") ?? "";
  const pos = useRows("po");
  const vendors = useRows("vendor");
  const linesByPo = useLinesByPo();
  const emails = useRows("po_email");
  const replies = useRows("po_email_reply");
  const accounts = useRows("gmail_account");
  const [creating, setCreating] = useState(false);
  const unseen = useUnseenUpdates();

  const vendorById = useMemo(() => new Map(vendors.map((v) => [v.id, v])), [vendors]);
  const today = planToday();
  const lateCount = useMemo(() => pos.filter((p) => daysLate(p, today) > 0 && (!vendorId || p.vendor_id === vendorId)).length, [pos, today, vendorId]);
  // Each PO's follow-up, worked out again when one of its inputs changes (the clock only moves "2h ago").
  const follows = useMemo(() => {
    const emailsOf = byPo(emails);
    const repliesOf = byPo(replies);
    const now = new Date().toISOString();
    return new Map(
      pos.map((p) => [
        p.id,
        followUp(p, { lines: linesByPo.get(p.id) ?? [], emails: emailsOf.get(p.id) ?? [], replies: repliesOf.get(p.id) ?? [], accounts }, now, today),
      ]),
    );
  }, [pos, linesByPo, emails, replies, accounts, today]);
  const followCount = useMemo(() => pos.filter((p) => follows.get(p.id)?.needs && (!vendorId || p.vendor_id === vendorId)).length, [pos, follows, vendorId]);
  const rows = useMemo<Row[]>(
    () =>
      pos
        .filter(
          (p) =>
            (status === "all"
              ? true
              : status === "open"
                ? isOpen(p.status)
                : status === "late"
                  ? daysLate(p, today) > 0
                  : status === "follow"
                    ? !!follows.get(p.id)?.needs
                    : status === "news"
                      ? unseen.has(p.id)
                      : p.status === status) &&
            (!vendorId || p.vendor_id === vendorId),
        )
        .map((p) => {
          const totals = poTotals(linesByPo.get(p.id) ?? [], p.deposit_pct);
          return {
            ...p,
            vendor: vendorLabel(vendorById.get(p.vendor_id)),
            lines: totals.lines,
            units: totals.qty,
            total: totals.subtotal,
            late: daysLate(p, today),
            follow: follows.get(p.id) as FollowUp,
            news: unseen.get(p.id) ?? 0,
          };
        }),
    [pos, status, vendorId, linesByPo, vendorById, today, follows, unseen],
  );

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: "number",
        header: "PO",
        width: "96px",
        value: (p) => Number(p.number.replace(/\D/g, "")) || 0,
        text: (p) => p.number,
        cell: (p) => <span className="font-mono text-[12px] font-medium">{p.number}</span>,
      },
      { key: "vendor", header: "Vendor", width: "minmax(150px,1.2fr)" },
      {
        key: "status",
        header: "Status",
        width: "130px",
        value: (p) => PO_STATUSES.indexOf(p.status),
        text: (p) => PO_STATUS_LABELS[p.status],
        cell: (p) => (
          <span className="flex items-center gap-1">
            <Badge tone={STATUS_TONE[p.status]}>{PO_STATUS_LABELS[p.status]}</Badge>
            {p.sent_revision !== null && p.revision > p.sent_revision && <Badge tone="warn">Rev {p.revision}</Badge>}
          </span>
        ),
      },
      { key: "total", header: "Total", width: "120px", align: "right", cell: (p) => money(p.total, p.currency) },
      { key: "lines", header: "Lines", width: "70px", align: "right" },
      { key: "units", header: "Units", width: "90px", align: "right", cell: (p) => qty(p.units) },
      {
        key: "next_due",
        header: "Next due",
        headerTitle:
          "What the vendor owes next: shipping it (its ship date), then its arrival (the earliest open line's promised date, else Needed by). Red once past.",
        width: "150px",
        value: (p) => p.follow.nextDue?.date ?? "9999",
        text: (p) => (p.follow.nextDue ? `${p.follow.nextDue.kind === "ship" ? "Ships" : "Arrives"} ${p.follow.nextDue.date}` : ""),
        cell: (p) => <NextDueCell f={p.follow} />,
      },
      {
        key: "answer",
        header: "Answer",
        headerTitle: `The vendor's side: Acknowledged, their latest reply (opens the Gmail thread), or how long it's gone without one (${NO_ANSWER_DAYS}+ days needs a nudge)`,
        width: "150px",
        value: (p) => answerRank(p.follow),
        text: (p) => answerText(p.follow),
        cell: (p) => <AnswerCell f={p.follow} />,
      },
      {
        key: "vendor_says",
        header: "They say",
        headerTitle: "When the vendor says it ships (amber when it's later than asked), and their updates nobody has opened yet",
        width: "130px",
        value: (p) => (p.news ? `0${p.vendor_ship_date ?? ""}` : (p.vendor_ship_date ?? "9999")),
        text: (p) => [p.vendor_ship_date ?? "", p.news ? `${p.news} new` : ""].filter(Boolean).join(" · "),
        cell: (p) => {
          const later = !!(p.vendor_ship_date && p.expected_ship && p.vendor_ship_date > p.expected_ship);
          return (
            <span className="flex items-center gap-1.5 truncate">
              {p.vendor_ship_date && (
                <span
                  className={later ? "text-warn" : "text-muted"}
                  title={later ? `They say it ships ${dayText(p.vendor_ship_date)}; asked for ${dayText(p.expected_ship)}` : undefined}
                >
                  {dayText(p.vendor_ship_date)}
                </span>
              )}
              {p.news > 0 && <Badge tone="accent">{p.news} new</Badge>}
            </span>
          );
        },
      },
      {
        key: "email",
        header: "Email",
        headerTitle: "A problem with the PO's email: it didn't go, we can't tell whether it went, or its mailbox needs reconnecting",
        width: "84px",
        value: (p) => (p.follow.problem ? 0 : 1),
        text: (p) => p.follow.problem?.message ?? "",
        cell: (p) =>
          p.follow.problem && (
            <span title={p.follow.problem.message}>
              <Badge tone={p.follow.problem.kind === "failed" ? "danger" : "warn"}>{EMAIL_PROBLEM[p.follow.problem.kind]}</Badge>
            </span>
          ),
      },
      {
        key: "expected_arrive",
        header: "Needed by",
        headerTitle: "When HHL needs it (the PO's required date); red once an open PO is past it",
        width: "170px",
        cell: (p) =>
          p.late ? (
            <span className="truncate text-danger" title={`Needed by ${dayText(p.expected_arrive)} and not received`}>
              {dayText(p.expected_arrive)} · {plural(p.late, "day")} late
            </span>
          ) : (
            <span className="text-muted">{dayText(p.expected_arrive)}</span>
          ),
      },
      { key: "kind", header: "Kind", width: "90px", value: (p) => PO_KIND_LABELS[p.kind] },
      { key: "updated_at", header: "Updated", width: "130px", cell: (p) => <span className="text-muted">{ago(p.updated_at)}</span> },
    ],
    [],
  );

  const vendorOptions = useMemo(() => {
    const used = new Set(pos.map((p) => p.vendor_id));
    return [
      { value: "", label: "All vendors" },
      ...vendors
        .filter((v) => used.has(v.id) || v.id === vendorId)
        .map((v) => ({ value: v.id, label: vendorLabel(v) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [pos, vendors, vendorId]);

  const activeVendors = useMemo(() => vendors.filter((v) => !v.archived).sort((a, b) => vendorLabel(a).localeCompare(vendorLabel(b))), [vendors]);

  return (
    <>
      <ListPage
        title="Purchase orders"
        rows={rows}
        columns={columns}
        defaultSort={{ key: "number", desc: true }}
        newLabel="New PO"
        onNew={can("po.create") ? () => setCreating(true) : undefined}
        pane="po"
        toolbar={
          // Each in its own box (shrink-0, at its own width): a Select is w-full, which would stretch it across a header's second row
          <>
            <div className="shrink-0">
              <Select
                aria-label="Vendor"
                value={vendorId}
                options={vendorOptions}
                onChange={(e) => setSearch({ vendor: e.target.value || null })}
                className="h-7 w-44 text-[12px]"
              />
            </div>
            <div className="shrink-0">
              <Select
                aria-label="Status"
                value={status}
                options={STATUS_FILTERS.map((f) =>
                  f.value === "late"
                    ? { ...f, label: `Late (${lateCount})` }
                    : f.value === "follow"
                      ? { ...f, label: `Needs follow-up (${followCount})` }
                      : f.value === "news"
                        ? { ...f, label: `New from vendors (${unseen.size})` }
                        : f,
                )}
                onChange={(e) => setSearch({ status: e.target.value === "open" ? null : e.target.value })}
                className="h-7 w-48 text-[12px]"
              />
            </div>
          </>
        }
        empty={
          <div className="p-6 text-center text-muted">
            {status === "late"
              ? "Nothing late: every open PO is due on or after today."
              : status === "follow"
                ? "Nothing to chase: every open PO is answered, on time and emailed fine."
                : status === "news"
                  ? "Nothing new from vendors: every update they've sent has been opened."
                  : pos.length
                    ? "No POs match these filters."
                    : "No purchase orders yet. Press N, or open a vendor and use New PO from their items."}
          </div>
        }
      />
      {creating && (
        <CreateDialog
          title="New purchase order"
          fields={[
            {
              key: "vendor_id",
              label: "Vendor",
              type: "select",
              options: [{ value: "", label: "Choose a vendor…" }, ...activeVendors.map((v) => ({ value: v.id, label: vendorLabel(v) }))],
            },
            {
              key: "kind",
              label: "Kind",
              type: "select",
              options: [
                { value: "purchase", label: "Purchase (parts and products)" },
                { value: "make", label: "Make (a CM builds from consigned parts)" }, // Consignment ---
                { value: "service", label: "Service (tooling, freight, one-off charges)" },
              ],
            },
          ]}
          initial={{ vendor_id: vendorId }}
          onClose={() => setCreating(false)}
          onSubmit={async (v) => {
            const vendor = vendors.find((x) => x.id === v.vendor_id);
            if (!vendor) {
              toast.error("Choose a vendor");
              return false;
            }
            if (v.kind === "make") return createMakePo(vendor); // Consignment ---
            return !!(await createPo(vendor, [], v.kind as "purchase" | "service"));
          }}
        />
      )}
    </>
  );
}

const EMAIL_PROBLEM = { failed: "Didn't go", unsure: "Went?", reconnect: "Reconnect" } as const;

/** The Answer column in words, for Copy / CSV. */
const answerText = (f: FollowUp) => {
  const v = f.vendor;
  return v.kind === "acknowledged"
    ? `Acknowledged ${v.at.slice(0, 10)}`
    : v.kind === "replied"
      ? `Replied ${v.at.slice(0, 10)}`
      : v.kind === "no_answer" || v.kind === "not_acknowledged"
        ? `No answer ${v.days}d`
        : "";
};

/** Sorting the Answer column: what needs chasing first. */
const answerRank = (f: FollowUp) =>
  f.vendor.kind === "replied"
    ? 0
    : f.vendor.kind === "no_answer" || f.vendor.kind === "not_acknowledged"
      ? 1000 - f.vendor.days
      : f.vendor.kind === "acknowledged"
        ? 2000
        : 3000;

function NextDueCell({ f }: { f: FollowUp }) {
  const d = f.nextDue;
  if (!d) return <span className="text-faint">—</span>;
  const what = d.kind === "ship" ? "Ships" : "Arrives";
  return (
    <span
      className={cx("truncate", d.daysLate ? "text-danger" : "text-muted")}
      title={d.daysLate ? `${what} ${dayText(d.date)}: ${plural(d.daysLate, "day")} past` : undefined}
    >
      {what} {dayText(d.date)}
      {d.daysLate > 0 && ` · ${d.daysLate}d late`}
    </span>
  );
}

function AnswerCell({ f }: { f: FollowUp }) {
  const v = f.vendor;
  const title = f.reasons.length ? f.reasons.join("\n") : undefined;
  // A link inside the row opens Gmail, not the PO.
  const gmail = (url: string | null, text: string, className: string) =>
    url ? (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={cx("inline-flex items-center gap-0.5 hover:underline", className)}
        title={title}
      >
        {text} <ExternalLink className="size-3 shrink-0" />
      </a>
    ) : (
      <span className={className} title={title}>
        {text}
      </span>
    );
  switch (v.kind) {
    case "acknowledged":
      return (
        <span className="inline-flex items-center gap-1 text-ok" title={`Acknowledged ${dayText(v.at)}`}>
          <Check className="size-3.5" /> Acknowledged
        </span>
      );
    case "replied":
      return gmail(v.url, `Replied ${agoShort(v.at)} ago`, "font-medium text-accent-text");
    case "no_answer":
      return gmail(
        v.url,
        v.days >= NO_ANSWER_DAYS ? `No answer ${v.days}d` : `Sent ${agoShort(v.since)} ago`,
        v.days >= NO_ANSWER_DAYS ? "text-warn" : "text-muted",
      );
    case "not_acknowledged":
      return (
        <span
          className={v.days >= NO_ANSWER_DAYS ? "text-warn" : "text-muted"}
          title={`Marked as sent ${ago(v.since)}, not emailed from HHL Ops, so replies can't be seen here${title ? `\n${title}` : ""}`}
        >
          {v.days >= NO_ANSWER_DAYS ? `Not acknowledged ${v.days}d` : `Sent ${agoShort(v.since)} ago`}
        </span>
      );
    default:
      return <span className="text-faint">—</span>;
  }
}
